<script lang="ts">
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import Alert from '../common/bootstrap/Alert.svelte';
  import FormModal from '../common/modal/FormModal.svelte';
  import { faWarning } from '@itdev-fortawesome/pro-solid-svg-icons/faWarning';
  import { faPlus } from '@itdev-fortawesome/pro-solid-svg-icons/faPlus';
  import { confirmStudentImport, prepareStudentImport } from '$lib/api/classes';
  import { ValidationError, type PreparedImportFile } from '$lib/api/types';
  import { faCheck } from '@itdev-fortawesome/pro-solid-svg-icons/faCheck';

  interface Props {
    isBulkImporting: boolean;
    classId: number;
  }

  let { isBulkImporting = $bindable(), classId }: Props = $props();

  let fileInputValidationError: string | null = $state(null);
  let fileInputInvalid = $derived(fileInputValidationError !== null);

  let selectedFile: File | null = null;

  let preparedImportFile: PreparedImportFile | null = $state(null);
</script>

<FormModal
  formId="upload_student_import_file"
  onclose={() => {
    if (preparedImportFile === null) {
      // set isBulkImporting to detach component
      isBulkImporting = false;
    }
  }}
  header="Bulk Import"
  submitBtnLabel="Upload"
  validationChecks={() => {
    if (selectedFile === null) {
      fileInputValidationError = 'Please choose a file to upload.';
      return false;
    }
    return true;
  }}
  onsubmit={async () => {
    try {
      preparedImportFile = await prepareStudentImport(classId, selectedFile!);
      return true;
    } catch (e) {
      if (e instanceof ValidationError) {
        fileInputValidationError = e.errors['file'].join(', ');
        return false;
      } else {
        throw e;
      }
    }
  }}
>
  <div class="mb-3">
    <label for="formFile" class="form-label is-required">Choose CSV or Excel File</label>
    <input
      onchange={e => {
        if (!e.currentTarget.files || e.currentTarget.files.length === 0) {
          fileInputValidationError = 'No file selected.';
          return;
        }

        if (e.currentTarget.files.length > 1) {
          fileInputValidationError = 'Please choose only one file.';
          return;
        }

        const file = e.currentTarget.files[0];

        if (
          file.type !== 'text/csv' &&
          file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
          fileInputValidationError = 'Selected file is not a CSV or Excel file.';
          return;
        }

        selectedFile = file;
      }}
      class="form-control"
      class:is-invalid={fileInputInvalid}
      aria-invalid={fileInputInvalid ? 'true' : 'false'}
      aria-errormessage={fileInputInvalid ? 'file_invalid_feedback' : undefined}
      type="file"
      id="formFile"
      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
    />
    <div class="invalid-feedback" id="file_invalid_feedback">{fileInputValidationError}</div>
  </div>
  <p>Choose a file to add students to a class. CSV or Excel files are accepted with the following columns:</p>
  <table class="table table-sm table-bordered text-center">
    <tbody>
      <tr>
        <td>First Name</td>
        <td>Last Name</td>
        <td>Student ID</td>
        <td>Clinical Group</td>
      </tr>
    </tbody>
  </table>
  <p>
    The import will fail if the column names above do not match or they are arranged in a different order. Clinical
    groups are identified by name. If any clinical group in the import file does not exist with the class, it will be
    created, and instructors can be assigned to the clinical groups after the import is complete.
  </p>
</FormModal>

{#if preparedImportFile}
  <FormModal
    scrollable
    size="lg"
    formId="confirm_student_import"
    onclose={() => {
      isBulkImporting = false;
    }}
    header="Bulk Import"
    submitBtnLabel="Import Students"
    invalidateUrl={url => url.pathname.startsWith('/api/classes')}
    onsubmit={async () => {
      await confirmStudentImport(classId, preparedImportFile!.operationId);
      return true;
    }}
  >
    {#if preparedImportFile.issues.length > 0}
      <Alert color="warning">
        <div class="d-flex gap-3 align-items-center small">
          <FontAwesomeIcon icon={faWarning} />
          <div>Issues were found with the following students:</div>
        </div>
      </Alert>

      <table class="table table-sm">
        <thead>
          <tr class="border-bottom">
            <th scope="col">Student Name</th>
            <th class="text-end text-nowrap" scope="col">Student ID</th>
            <th scope="col">Issue</th>
          </tr>
        </thead>
        <tbody>
          {#each preparedImportFile.issues as issue}
            <tr>
              <td>{issue.name}</td>
              <td class="text-end">{issue.studentId}</td>
              <td>{issue.message}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}

    <p>
      {preparedImportFile?.studentCount} student(s) will be imported into this class. Import file contains {preparedImportFile
        .clinicalGroups.length} clinical group(s):
    </p>

    <table class="table">
      <tbody>
        {#each preparedImportFile.clinicalGroups as cg}
          <tr>
            <td>{cg.name}</td>
            <td class="text-end">
              {#if cg.exists}
                <span class="badge text-bg-success">
                  <FontAwesomeIcon icon={faCheck} />
                  Exists
                </span>
              {:else}
                <span class="badge text-bg-secondary">
                  <FontAwesomeIcon icon={faPlus} />
                  Will be created
                </span>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </FormModal>
{/if}
